import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EllipsisIcon, PackageIcon, PencilIcon, PlusIcon, XIcon } from 'lucide-react';
import { Button, ConfirmDialog, DataTable, EmptyState, ErrorNotice, LoadingState, Panel, Pill, SearchInput, type DataTableSort } from '@bcecloud/portal-ui';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { errorMessage } from '@/lib/api';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { deleteSupplyItem, listSupplyItems, vendorsText, type SupplyItem } from '@/lib/supplies';

const SORT_FIELDS = { name: 'name', vendors: 'vendors', unit: 'unit' } as const;

/** The list row: the item plus its vendors as one sortable, searchable string. */
interface SupplyRow extends SupplyItem {
  readonly vendors: string;
}
const SORT_KEYS = Object.values(SORT_FIELDS);
const DEFAULT_SORT: DataTableSort = { key: SORT_FIELDS.name, direction: 'asc' };

/**
 * Settings → Supplies: the catalog every program kit draws from — name,
 * picture, vendor and where to reorder. Internal roles.
 */
export function SuppliesTab(): React.ReactElement {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [removing, setRemoving] = useState<SupplyItem | null>(null);
  const { sort, onSort } = useTableSort(SORT_KEYS, DEFAULT_SORT);
  const items = useQuery({ queryKey: ['supplies'], queryFn: ({ signal }) => listSupplyItems(signal) });
  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['supplies'] });
  }
  const remove = useMutation({
    mutationFn: (item: SupplyItem) => deleteSupplyItem(item._id),
    onSuccess: async (_result, item) => {
      toast.success(`Removed ${item.name}`);
      setRemoving(null);
      await refresh();
    },
  });
  const rows = items.data ? sortRows(filterRows(items.data.map((item): SupplyRow => ({ ...item, vendors: vendorsText(item.sources) })), search, ['name', 'vendors', 'description']), sort) : [];

  return (
    <div className="mt-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SearchInput data-guide="supplies-search" label="Search supplies" placeholder="Item, vendor or description" value={search} onChange={(event) => setSearch(event.target.value)} className="w-full max-w-sm" />
        <span data-guide="supplies-new">
          <Button to="/settings/supplies/new">
            <PlusIcon aria-hidden className="size-4" /> New item
          </Button>
        </span>
      </div>
      {items.isPending ? (
        <LoadingState label="Loading supplies…" />
      ) : items.isError ? (
        <ErrorNotice title="Supplies unavailable" message={errorMessage(items.error)} />
      ) : items.data.length === 0 ? (
        <Panel>
          <EmptyState icon={<PackageIcon aria-hidden className="size-6" />} title="No supplies yet" message="Add what the warehouse packs — bins, medals, signs — with a picture and a link to reorder." action={<Button to="/settings/supplies/new">New item</Button>} />
        </Panel>
      ) : (
        <div data-guide="supplies-table"><DataTable headers={[{ label: 'Item', sortKey: SORT_FIELDS.name }, { label: 'Where to order', sortKey: SORT_FIELDS.vendors }, { label: 'Unit', sortKey: SORT_FIELDS.unit }, 'Status', 'Actions']} visuallyHiddenHeaders={[4]} sort={sort} onSort={onSort} minWidth={760}>
          {rows.map((item) => (
            <tr key={item._id} className="border-t border-portal-line align-middle">
              <td className="p-3 text-portal-small">
                <span className="flex items-center gap-3">
                  {item.imageUrl ? <img src={item.imageUrl} alt="" className="size-10 shrink-0 rounded-md border border-portal-line object-cover" /> : <span className="grid size-10 shrink-0 place-items-center rounded-md border border-portal-line text-portal-muted"><PackageIcon aria-hidden className="size-4" /></span>}
                  <span className="min-w-0">
                    <span className="block font-bold">{item.name}</span>
                    {item.description ? <span className="block max-w-[360px] truncate text-portal-micro text-portal-muted">{item.description}</span> : null}
                  </span>
                </span>
              </td>
              <td className="p-3 text-portal-small">
                {item.sources.length === 0 ? (
                  <span className="text-portal-muted">—</span>
                ) : (
                  <span className="flex flex-wrap gap-x-2 gap-y-0.5">
                    {item.sources.map((source, index) =>
                      source.url ? (
                        <a key={`${source.vendor}-${index}`} href={source.url} target="_blank" rel="noreferrer" className="text-brand hover:underline">
                          {source.vendor}
                        </a>
                      ) : (
                        <span key={`${source.vendor}-${index}`} className="text-portal-muted">{source.vendor}</span>
                      ),
                    )}
                  </span>
                )}
              </td>
              <td className="p-3 text-portal-small text-portal-muted">{item.unit || '—'}</td>
              <td className="p-3 text-portal-small">
                <Pill tone={item.isActive ? 'success' : 'neutral'}>{item.isActive ? 'Active' : 'Inactive'}</Pill>
              </td>
              <td className="p-3 text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" data-guide="supplies-row-menu" aria-label={`Actions for ${item.name}`} className="grid size-7 place-items-center rounded-md text-portal-muted hover:bg-admin-subtle hover:text-portal-ink">
                      <EllipsisIcon aria-hidden className="size-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => navigate(`/settings/supplies/${item._id}`)}>
                      <PencilIcon aria-hidden className="size-4" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem variant="destructive" onSelect={() => setRemoving(item)}>
                      <XIcon aria-hidden className="size-4" /> Remove
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
          ))}
        </DataTable></div>
      )}
      <ConfirmDialog
        open={removing !== null}
        tone="danger"
        title={`Remove ${removing?.name ?? 'this item'}?`}
        message="Only possible when no program kit uses it; packing lists already built keep their copy."
        confirmLabel="Remove"
        pending={remove.isPending}
        error={remove.isError ? errorMessage(remove.error) : undefined}
        onConfirm={() => {
          if (removing) remove.mutate(removing);
        }}
        onCancel={() => {
          if (!remove.isPending) setRemoving(null);
        }}
      />
    </div>
  );
}
